import React from 'react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

import { Building2, AlertCircle, ArrowLeft, Plus, RefreshCw, Loader2, HardHat, ShieldCheck, Scale } from 'lucide-react';

import CcmiCreateDialog from '../CcmiDashboard_CreateDialog';
import CcmiDetailDialog from '../CcmiDashboard_DetailDialog';
import CcmiComplianceDialog from '../CcmiDashboard_ComplianceDialog';
import CcmiJuristeDialog from '../CcmiDashboard_JuristeDialog';
import CcmiPaywallDialog from '../CcmiDashboard_PaywallDialog';
import { CCMI_COMPLIANCE_CHECKLIST, computeComplianceStatus } from './ccmiConstants';
import { useCcmiDashboard } from './useCcmiDashboard';

export { CCMI_COMPLIANCE_CHECKLIST, computeComplianceStatus } from './ccmiConstants';

export default function CcmiDashboard() {
  const ctx = useCcmiDashboard();
  const { counts, filterStatut, items, loadError, loading, search, setFilterStatut, setSearch, setSelected, setShowCreate, fetchList, navigate, STATUT_META } = ctx;
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-slate-50 py-8 px-4" data-testid="ccmi-dashboard">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 rounded-lg hover:bg-white/80 transition-colors border border-slate-200"
              title="Retour"
              data-testid="ccmi-back-btn"
            >
              <ArrowLeft className="h-5 w-5 text-slate-700" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
                <HardHat className="h-7 w-7 text-indigo-600" />
                Cockpit CCMI
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Contrats de Construction de Maison Individuelle — Passerelle interne vers DOS DOMIUM.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchList} disabled={loading} data-testid="ccmi-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Rafraîchir
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/juriste/queue')} data-testid="ccmi-open-juriste-queue-btn">
              <ShieldCheck className="h-4 w-4 mr-1" />
              File juriste
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/legalcoop')} data-testid="ccmi-open-legalcoop-btn">
              <Scale className="h-4 w-4 mr-1" />
              LEGALCOOP
            </Button>
            <Button onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ccmi-create-btn">
              <Plus className="h-4 w-4 mr-1" />
              Nouveau projet
            </Button>
          </div>
        </div>

        {/* KPI counters */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ccmi-counters">
          {['brouillon', 'transmis_domium', 'signe', 'archive'].map((k) => {
            const meta = STATUT_META[k];
            const Icon = meta.Icon;
            return (
              <Card key={k} className="border-slate-200">
                <CardContent className="py-4 flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${meta.color.split(' ')[0]}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">{counts[k] || 0}</div>
                    <div className="text-xs text-slate-500">{meta.label}</div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          <Card className="border-indigo-300 bg-indigo-50/60">
            <CardContent className="py-4 flex items-center gap-3">
              <Building2 className="h-5 w-5 text-indigo-700" />
              <div>
                <div className="text-2xl font-bold text-indigo-900">{counts.actifs || 0}</div>
                <div className="text-xs text-indigo-700">Actifs (hors archive)</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="py-4 flex flex-wrap items-end gap-3">
            <div className="min-w-[200px]">
              <Label className="text-xs text-slate-500">Statut</Label>
              <Select value={filterStatut} onValueChange={setFilterStatut}>
                <SelectTrigger
                  className="font-semibold text-slate-900 border-slate-400 bg-white"
                  data-testid="ccmi-filter-statut"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous (avec archivés)</SelectItem>
                  <SelectItem value="brouillon">Brouillon</SelectItem>
                  <SelectItem value="transmis_domium">Transmis DOMIUM</SelectItem>
                  <SelectItem value="signe">Signé</SelectItem>
                  <SelectItem value="archive">Archivés</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs text-slate-500">Recherche référence</Label>
              <Input
                placeholder="CCMI-..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchList()}
                data-testid="ccmi-filter-search"
              />
            </div>
            <Button onClick={fetchList} disabled={loading} data-testid="ccmi-apply-filters">
              Appliquer
            </Button>
          </CardContent>
        </Card>

        {/* List */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Projets CCMI</CardTitle>
            <CardDescription>{items.length} projet(s) affiché(s)</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-indigo-500" /></div>
            ) : loadError ? (
              <div className="py-12 text-center" data-testid="ccmi-load-error">
                <AlertCircle className="h-10 w-10 mx-auto text-red-500 mb-2" />
                <p className="text-sm text-red-700 font-medium mb-2">{loadError}</p>
                <Button onClick={fetchList} variant="outline" size="sm" className="mt-2">
                  <RefreshCw className="h-4 w-4 mr-1" /> Réessayer
                </Button>
              </div>
            ) : items.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                <Building2 className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                Aucun projet CCMI. Cliquez sur <strong>Nouveau projet</strong> pour commencer.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="ccmi-list">
                  <thead className="bg-slate-50 border-b">
                    <tr className="text-left">
                      <th className="p-3 font-semibold">Référence</th>
                      <th className="p-3 font-semibold">Maître d&apos;ouvrage</th>
                      <th className="p-3 font-semibold">Constructeur</th>
                      <th className="p-3 font-semibold">Prix TTC</th>
                      <th className="p-3 font-semibold">Type</th>
                      <th className="p-3 font-semibold">Statut</th>
                      <th className="p-3 font-semibold">Conformité</th>
                      <th className="p-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((p, idx) => {
                      const meta = STATUT_META[p.statut] || STATUT_META.brouillon;
                      const missing = (p.compliance_guards_missing || []).length;
                      const StatusIcon = meta.Icon;
                      return (
                        <tr key={p.id} className="border-b hover:bg-indigo-50/40" data-testid={`ccmi-row-${idx}`}>
                          <td className="p-3 font-mono text-xs">{p.reference}</td>
                          <td className="p-3">
                            {p.maitre_ouvrage?.nom || '-'} {p.maitre_ouvrage?.prenom || ''}
                          </td>
                          <td className="p-3 text-slate-600">{p.constructeur?.raison_sociale || '-'}</td>
                          <td className="p-3 tabular-nums">{p.prix_ttc ? `${Number(p.prix_ttc).toLocaleString('fr-FR')} €` : '-'}</td>
                          <td className="p-3 text-xs text-slate-600">{p.type_contrat || '-'}</td>
                          <td className="p-3">
                            <Badge className={`text-[10px] ${meta.color} border gap-1`}>
                              <StatusIcon className="h-3 w-3" /> {meta.label}
                            </Badge>
                          </td>
                          <td className="p-3">
                            {missing === 0 ? (
                              <Badge className="text-[10px] bg-emerald-700 text-white border-emerald-900 font-semibold">Complète</Badge>
                            ) : (
                              <Badge className="text-[10px] bg-amber-600 text-white border-amber-800 font-semibold">
                                {missing} manquant{missing > 1 ? 's' : ''}
                              </Badge>
                            )}
                          </td>
                          <td className="p-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelected(p)}
                              data-testid={`ccmi-open-${idx}`}
                            >
                              Ouvrir
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modale création */}
      <CcmiCreateDialog ctx={ctx} />

      {/* Modale détail */}
      <CcmiDetailDialog ctx={ctx} />

      {/* V37.55 — Modal "Mise en conformite" */}
      <CcmiComplianceDialog ctx={ctx} />

      {/* V37.56 — Modal Assignation Juriste */}
      <CcmiJuristeDialog ctx={ctx} />

      {/* V37.71 — Modale paywall : Contrat définitif CCMI (500 € HT) */}
      <CcmiPaywallDialog ctx={ctx} />
    </div>
  );
}
